import React, { useState } from 'react';
import { Modal, Typography } from 'antd';
import Row from '../../components-v2/Row';
import Col from '../../components-v2/Col';
import apiClient from '../../utils/apiClient';
import { showNotification, NotificationTypes } from '../../components/Notifications';

const { Title } = Typography;

interface ResetConfigModalWrapperProps {
  author: string;
  integrationType?: 'clever' | 'oneRoster';
  setTTLFlag?: boolean;
  onSuccess?: () => void;
  /** Optional: external control of modal visibility */
  visible?: boolean;
  onClose?: () => void;
  /** If true, renders only the modal (caller controls it externally) */
  modalOnly?: boolean;
  /** If false, don't render card and button */
  hideCard?: boolean;
  /** Conditional visibility of entire component */
  hideIf?: boolean;
  /** Function to set visibility of the reset config */
  setShowResetConfig?: (show: boolean) => void;
}

const ResetConfigModalWrapper: React.FC<ResetConfigModalWrapperProps> = ({
  author,
  integrationType = 'oneRoster',
  setTTLFlag = false,
  onSuccess,
  visible,
  onClose,
  modalOnly = false,
  hideCard = false,
  hideIf = false,
  setShowResetConfig,
}) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const isControlled = typeof visible === 'boolean';
  const modalVisible = isControlled ? visible : internalVisible;

  const openModal = () => {
    if (!isControlled) setInternalVisible(true);
  };

  const closeModal = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setInternalVisible(false);
    }
  };

  const handleReset = async () => {
    try {
      await apiClient.delete('/data-ingest/reset-config', {
        data: {
          author,
          setTTL: setTTLFlag,
        },
      });

      if (setTTLFlag) {
        showNotification(
          NotificationTypes.info,
          'Reset started',
          `The ${integrationType === 'clever' ? 'Clever import' : 'SIS import'} configuration reset has started and it will take a few minutes to complete. Do not set up a new integration until the reset is done.`,
          {
            backgroundColor: '#E8F0FD',
            borderRadius: '8px',
            border: '1px solid #1162EE',
            padding: '16px',
            width: 'fit-content',
            height: '100px',
          },
          null,
          4,
          'top'
        );
      } else {
        showNotification(NotificationTypes.success, 'Success', 'Mapping updated successfully');
      }

      if (setShowResetConfig) {
        setShowResetConfig(false);
      }
      closeModal();
      onSuccess?.();
    } catch (error) {
      showNotification(NotificationTypes.error, 'Error Resetting Configuration', 'Failure in saving data on server.');
      closeModal();
    }
  };

  if (hideIf) return null;

  return (
    <>
      {!modalOnly && !hideCard && (
        <Row className="reset-div-row">
          <Col span={18} data-testid="reset-config-col">
            <span>
              If you currently have a Clever or Powerschool Data Integration set up in Naviance, you must click <b>Reset Configuration</b> before continuing with the setup to ensure a successful data import. Your existing Naviance data will not be deleted, but your automated sync will be disabled.
            </span>
          </Col>
          <Col span={4} data-testid="reset-config-col">
            <button
              className="reset-config-btn"
              data-testid="reset-one-roster-configuration"
              onClick={openModal}
            >
              Reset current configuration
            </button>
          </Col>
        </Row>
      )}

      <Modal
        open={modalVisible}
        onCancel={closeModal}
        onOk={handleReset}
        okText="Reset Configuration"
        cancelText="Cancel"
        className="modal"
        style={{ marginTop: '15%', position: 'revert', padding: '0px' }}
      >
        <Title level={5}>Reset Current Configuration?</Title>
        <span>
          Data currently in Naviance will remain but auto-sync will be disabled. OneRoster will need to be mapped
          in <b>Oneroster Import Settings</b>.
        </span>
      </Modal>
    </>
  );
};

export default ResetConfigModalWrapper;
